import type { TInitFunction } from 'scichart-react';
import {
  SciChartSurface,
  TSciChart,
  NumericAxis,
  DateTimeNumericAxis,
  CategoryAxis,
  TextLabelProvider,
  EAxisAlignment,
  NumberRange,
  ELabelAlignment,
  EAutoRange,
  BoxAnnotation,
  ECoordinateMode,
  ZoomPanModifier,
  PinchZoomModifier,
  VerticalLineAnnotation,
  LineAnnotation,
  TextAnnotation,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  EXyDirection,
  ZoomExtentsModifier,
  AnnotationHoverModifier,
  EHoverMode,
  CursorModifier,
  TCursorTooltipDataTemplate,
  TTextStyle,
  Thickness,
  XyDataSeries,
  XyyDataSeries,
  FastLineRenderableSeries,
  FastBandRenderableSeries,
  CustomAnnotation,
} from 'scichart';
import moment from 'moment-timezone';
import { axisLabelDateFormatter } from '../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter';
import { DateTickProvider } from '../../../Shared/SciChart/SmartAxis/DateTickProvider';
import { SmartDateTZLabelProvider } from '../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider';
import { createDateTzConverter } from '../../../Shared/SciChart/SmartAxis/timezones';
import { GetChartTheme } from '../../../Shared/SciChart/ChartConfiguration';
import {
  ApiScheduleGroupRow,
  WORK_ORDERS_STATUS_DISPLAY_COLORS,
  WORK_ORDERS_WINDOW_BORDER_STROKE,
  WORK_ORDERS_LATE_BORDER_STROKE,
  WORK_ORDERS_LATE_PENDING_COLOR,
  STATUS_ORDER,
  StatusWithinWindow,
  SegmentInternal,
  ScheduleWindowInternal,
  ScheduleClickPayload,
  UnscheduleClickPayload,
  getWorkOrdersGroupYLabelLines,
} from './WorkOrdersTypes';

const dateToSeconds = (d: Date) => d.getTime() / 1000;

const STATUS_COLORS: Record<StatusWithinWindow, { fill: string; stroke: string }> = WORK_ORDERS_STATUS_DISPLAY_COLORS;

function sumRowTotals(group: ApiScheduleGroupRow): { actual: number; expected: number } | undefined {
  if (group.workOrders.length === 0) return undefined;
  let actual = 0;
  let expected = 0;
  for (const w of group.workOrders) {
    const s = w.schedule;
    const ls = w.lateSchedule;
    /** Actual = total tasks accounted for (all buckets, on-time + late, including pending). */
    actual += s.compliant + s.partial + s.nonCompliant + s.pending + (w.unschedule ?? 0);
    if (ls) actual += ls.compliant + ls.partial + ls.nonCompliant + ls.pending;
    expected += w.expected;
  }
  actual += group.unscheduleEvents?.reduce((sum, g) => sum + g.events.length, 0) ?? 0;
  return { actual, expected };
}

export interface WorkOrdersApiParams {
  apiData: ApiScheduleGroupRow[];
  /** Row grouping mode — Schedule merges assets, so Y labels show only the schedule. */
  groupBy?: 'AssetAndSchedule' | 'Schedule';
  sharedXRange: NumberRange;
  /** Pan/zoom bounds — union of the selected window and all returned data. */
  xRangeLimit: NumberRange;
  timezone?: string;
  onScheduleClick?: (p: ScheduleClickPayload) => void;
  onUnscheduleGroupClick?: (p: UnscheduleClickPayload) => void;
}

export interface WorkOrdersApiResult {
  initHeaderChart: TInitFunction<SciChartSurface>;
  initBodyChart: TInitFunction<SciChartSurface>;
}

const HEADER_AXIS_LABEL_HIDE: Partial<TTextStyle> = {
  fontSize: 0.01,
  color: '#FFFFFF',
};

/** Must match left/right on body surface so header X axis and body grid share the same seriesViewRect width */
const WORK_ORDERS_SURFACE_PADDING_LR = 10;

/** Two-line Y labels (asset + schedule) — `TextLabelProvider` line height multiplier */
const WORK_ORDERS_ROW_LABEL_LINE_SPACING = 1.85;
/** Match Work Orders legend + app `fontFamily.sans` in tailwind.config (Roboto first) and `text-sm text-slate-700` */
const WORK_ORDERS_ROW_LABEL_TEXT: Partial<TTextStyle> = {
  fontFamily: 'Roboto, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  fontSize: 12,
  fontWeight: '500',
  color: '#335155',
  alignment: ELabelAlignment.Left,
};
const WORK_ORDERS_ROW_TOTAL_TEXT: Partial<TTextStyle> = {
  fontFamily: 'Roboto, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  fontSize: 12,
  fontWeight: '400',
  color: '#475569',
  alignment: ELabelAlignment.Left,
};

/** Fixed header surface: date labels + optional light grid; paired with body via sync. */
function createWorkOrdersHeaderDateXAxis(wasmContext: TSciChart, sharedXRange: NumberRange, timezone: string, rangeLimit: NumberRange): DateTimeNumericAxis {
  const range = new NumberRange(sharedXRange.min, sharedXRange.max);
  const xAxis = new DateTimeNumericAxis(wasmContext, {
    id: 'xDate',
    visibleRange: range,
    visibleRangeLimit: new NumberRange(rangeLimit.min, rangeLimit.max),
    zoomExtentsToInitialRange: true,
    axisAlignment: EAxisAlignment.Top,
    axisThickness: 38,
    drawMajorGridLines: false,
    drawMinorGridLines: false,
    drawLabels: true,
    labelStyle: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: 12,
      fontWeight: 'bold',
      color: '#111827',
      alignment: ELabelAlignment.Center,
    },
  });
  xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
  xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
  xAxis.labelProvider = new SmartDateTZLabelProvider(timezone, true);
  const dateTzConverter = createDateTzConverter(timezone);
  xAxis.labelProvider.formatCursorLabel = (timestamp) =>
    axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter);
  return xAxis;
}

/** Scrollable body: X at bottom — grid only; labels live on the header surface. */
function createWorkOrdersBodyDateXAxis(wasmContext: TSciChart, sharedXRange: NumberRange, timezone: string, rangeLimit: NumberRange): DateTimeNumericAxis {
  const range = new NumberRange(sharedXRange.min, sharedXRange.max);
  const xAxis = new DateTimeNumericAxis(wasmContext, {
    id: 'xDate',
    visibleRange: range,
    visibleRangeLimit: new NumberRange(rangeLimit.min, rangeLimit.max),
    zoomExtentsToInitialRange: true,
    axisAlignment: EAxisAlignment.Bottom,
    axisThickness: 14,
    drawMajorGridLines: true,
    drawMinorGridLines: false,
    drawLabels: false,
    drawMajorTickLines: false,
    drawMinorTickLines: false,
  });
  xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
  xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
  return xAxis;
}

function addSharedWorkOrdersXZoomModifiers(sciChartSurface: SciChartSurface) {
  sciChartSurface.chartModifiers.add(
    new ZoomPanModifier({ xyDirection: EXyDirection.XDirection }),
    new ZoomExtentsModifier({
      xyDirection: EXyDirection.XDirection,
      isAnimated: true,
      animationDuration: 350,
    }),
    /**
     * No MouseWheelZoomModifier: the wheel must scroll the row list (the `overflow-y-auto`
     * region) on desktop, not zoom the X axis. X zoom is still available via the overview
     * navigator. PinchZoomModifier keeps two-finger pinch zoom on touch devices (iPad).
     */
    new PinchZoomModifier({ xyDirection: EXyDirection.XDirection })
  );
}

type AnnotationMeta =
  | {
      kind: 'segment';
      index: string | number;
      assetId: string | null;
      row: number;
      value: number;
      status: StatusWithinWindow;
      start: number;
      end: number;
    }
  | {
      kind: 'window';
      index: string | number;
      assetId: string | null;
      row: number;
      active: boolean;
      expected: number;
      unschedule: number;
      windowStart: number;
      windowEnd: number;
    }
  | {
      kind: 'untracked';
      row: number;
      occurred: number;
      count: number;
      /** Events whose RFID is all-zeros (no tag) */
      untrackedCount: number;
      /** Events with a real RFID tag */
      unscheduledCount: number;
    };

type WorkOrdersBoxAnnotation = BoxAnnotation & {
  workOrdersMeta?: AnnotationMeta;
  baseStroke?: string;
  baseStrokeThickness?: number;
  baseFill?: string;
};

type WorkOrdersHoverableAnnotation = (BoxAnnotation | CustomAnnotation) & {
  workOrdersMeta?: AnnotationMeta;
  baseStroke?: string;
  baseStrokeThickness?: number;
  baseFill?: string;
};

function emitScheduleClickPayload(
  win: ScheduleWindowInternal,
  apiData: ApiScheduleGroupRow[],
  onScheduleClick?: (p: ScheduleClickPayload) => void
) {
  if (!onScheduleClick) return;
  const group = apiData[win.row];
  const windowRow = group?.workOrders.find((c) => c.index === win.index);
  /** Backend sends the whole work order in one actions[] (each with isLate); the panel filters on it. */
  const actions = windowRow?.actions ?? [];
  const payload: ScheduleClickPayload = {
    complianceWindowIndex: win.index,
    assetId: win.assetId,
    scheduleName: group?.scheduleName ?? undefined,
    assetName: group?.assetName ?? undefined,
    row: win.row,
    active: win.active,
    /** Authoritative late signal from the backend (window-level `isLate`). */
    isOverdue: windowRow?.isLate === true,
    expected: win.expected,
    unschedule: win.unschedule,
    /** Full work-order span (start → full end), so the panel header covers on-time + late. */
    windowStart: new Date((windowRow ? dateToSeconds(new Date(windowRow.startDateUtc)) : win.start) * 1000),
    windowEnd: new Date((windowRow ? dateToSeconds(new Date(windowRow.endDateUtc)) : win.end) * 1000),
    actions,
  };
  onScheduleClick(payload);
}

export function getWorkOrdersAPI({
  apiData,
  groupBy,
  sharedXRange,
  xRangeLimit,
  timezone,
  onScheduleClick,
  onUnscheduleGroupClick,
}: WorkOrdersApiParams): WorkOrdersApiResult {
  const tz = timezone || moment.tz.guess();
  const initHeaderChart: TInitFunction<SciChartSurface> = async (rootElement) => {
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(rootElement, {
      theme: GetChartTheme(),
      /** Same horizontal inset as body — otherwise ticks and grid lines diverge */
      padding: new Thickness(0, WORK_ORDERS_SURFACE_PADDING_LR, 0, WORK_ORDERS_SURFACE_PADDING_LR),
    });

    const rowLabels = apiData.map((s) => getWorkOrdersGroupYLabelLines(s, groupBy));
    const rowCount = rowLabels.length;
    const yRange = new NumberRange(-0.5, rowCount - 0.5);

    const xAxis = createWorkOrdersHeaderDateXAxis(wasmContext, sharedXRange, tz, xRangeLimit);

    const yAxis = new CategoryAxis(wasmContext, {
      id: 'yAssets',
      axisAlignment: EAxisAlignment.Left,
      labelProvider: new TextLabelProvider({
        labels: rowLabels as any,
        lineSpacing: WORK_ORDERS_ROW_LABEL_LINE_SPACING,
      }),
      visibleRange: yRange,
      autoTicks: false,
      majorDelta: 1,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      drawMajorTickLines: false,
      drawMinorTickLines: false,
      labelStyle: {
        ...WORK_ORDERS_ROW_LABEL_TEXT,
        ...HEADER_AXIS_LABEL_HIDE,
      },
    });

    const totalAxis = new NumericAxis(wasmContext, {
      id: 'yTotal',
      axisAlignment: EAxisAlignment.Right,
      visibleRange: yRange,
      autoRange: EAutoRange.Never,
      autoTicks: false,
      majorDelta: 1,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      drawMinorTickLines: false,
      labelStyle: {
        ...WORK_ORDERS_ROW_TOTAL_TEXT,
        ...HEADER_AXIS_LABEL_HIDE,
      },
    });

    totalAxis.labelProvider.formatLabel = () => '';

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);
    sciChartSurface.yAxes.add(totalAxis);

    addSharedWorkOrdersXZoomModifiers(sciChartSurface);

    return { sciChartSurface };
  };

  const initBodyChart: TInitFunction<SciChartSurface> = async (rootElement) => {
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(rootElement, {
      theme: GetChartTheme(),
      /** Top = 0 flush with header; L/R must match header padding for X alignment */
      padding: new Thickness(0, WORK_ORDERS_SURFACE_PADDING_LR, 10, WORK_ORDERS_SURFACE_PADDING_LR),
    });

    const rootEl = typeof rootElement === 'string' ? document.getElementById(rootElement) : rootElement;
    if (rootEl instanceof HTMLElement) {
      rootEl.style.position = 'relative';
    }

    const rowLabels = apiData.map((s) => getWorkOrdersGroupYLabelLines(s, groupBy));

    const rowCount = rowLabels.length;
    const yRange = new NumberRange(-0.5, rowCount - 0.5);

    /** Solid (on-time) bar: startDateUtc → originalEndDateUtc (or endDateUtc when not late). */
    const windows: ScheduleWindowInternal[] = [];
    apiData.forEach((group, rowIndex) => {
      for (const cw of group.workOrders) {
        const solidEnd = cw.originalEndDateUtc ? new Date(cw.originalEndDateUtc) : new Date(cw.endDateUtc);
        windows.push({
          index: cw.index,
          assetId: group.assetId,
          row: rowIndex,
          start: dateToSeconds(new Date(cw.startDateUtc)),
          end: dateToSeconds(solidEnd),
          active: cw.active,
          expected: cw.expected,
          unschedule: cw.unschedule ?? 0,
        });
      }
    });

    /**
     * Late (dashed) region — one per work order that has a late window: originalEndDateUtc → endDateUtc,
     * counts from `lateSchedule`. Reuses the window machinery via a synthetic `<index>#late` key.
     */
    const lateKeyOf = (cwIndex: string | number) => `${cwIndex}#late`;
    const lateWindows: ScheduleWindowInternal[] = [];
    /** Keyed by `${row}|${lateKey}` — parent window + late counts for tooltips/clicks. */
    const lateMetaByKey = new Map<
      string,
      { parentIndex: string | number; counts: Record<StatusWithinWindow, number>; start: number; end: number }
    >();
    apiData.forEach((group, rowIndex) => {
      for (const cw of group.workOrders) {
        if (!cw.originalEndDateUtc || !cw.lateSchedule) continue;
        const start = dateToSeconds(new Date(cw.originalEndDateUtc));
        const end = dateToSeconds(new Date(cw.endDateUtc));
        if (end <= start) continue;
        const lk = lateKeyOf(cw.index);
        lateWindows.push({
          index: lk,
          assetId: group.assetId,
          row: rowIndex,
          start,
          end,
          active: cw.active,
          expected: 0,
          unschedule: 0,
        });
        lateMetaByKey.set(`${rowIndex}|${lk}`, {
          parentIndex: cw.index,
          counts: cw.lateSchedule,
          start,
          end,
        });
      }
    });

    const expandToSegments = (
      win: ScheduleWindowInternal,
      schedule: Record<StatusWithinWindow, number>
    ): SegmentInternal[] => {
      const { start, end } = win;
      const totalSpan = end - start;
      const totalValue = STATUS_ORDER.reduce((acc, s) => acc + (schedule[s] ?? 0), 0);
      if (totalSpan <= 0 || totalValue <= 0) return [];

      let cursor = start;
      const output: SegmentInternal[] = [];

      for (const status of STATUS_ORDER) {
        const value = schedule[status];
        if (!value) continue;
        const span = totalSpan * (value / totalValue);
        output.push({
          index: win.index,
          assetId: win.assetId,
          row: win.row,
          start: cursor,
          end: cursor + span,
          value,
          status,
        });
        cursor += span;
      }

      return output;
    };

    const allSegments: SegmentInternal[] = [];
    windows.forEach((win) => {
      const group = apiData[win.row];
      const cw = group.workOrders.find((c) => c.index === win.index);
      if (!cw) return;
      allSegments.push(...expandToSegments(win, cw.schedule));
    });

    /**
     * Late (dashed) segments: split by count across [originalEndDateUtc → endDateUtc] from `lateSchedule`
     * (non-compliant / partial / pending — never compliant; pending renders purple, last). It is ONE work
     * order — hover/click is the region-wide hit box below, not the individual segments.
     */
    lateWindows.forEach((lw) => {
      const meta = lateMetaByKey.get(`${lw.row}|${lw.index}`);
      if (!meta) return;
      allSegments.push(...expandToSegments(lw, meta.counts));
    });
    const isLateSeg = (seg: SegmentInternal) => lateMetaByKey.has(`${seg.row}|${seg.index}`);

    const xAxis = createWorkOrdersBodyDateXAxis(wasmContext, sharedXRange, tz, xRangeLimit);

    const yAxis = new CategoryAxis(wasmContext, {
      id: 'yAssets',
      axisAlignment: EAxisAlignment.Left,
      labelProvider: new TextLabelProvider({
        labels: rowLabels as any,
        lineSpacing: WORK_ORDERS_ROW_LABEL_LINE_SPACING,
      }),
      visibleRange: yRange,
      autoTicks: false,
      majorDelta: 1,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      drawMajorTickLines: false,
      drawMinorTickLines: false,
      drawMajorBands: false,
      labelStyle: WORK_ORDERS_ROW_LABEL_TEXT,
    });

    const totalAxis = new NumericAxis(wasmContext, {
      id: 'yTotal',
      axisAlignment: EAxisAlignment.Right,
      visibleRange: yRange,
      autoRange: EAutoRange.Never,
      autoTicks: false,
      majorDelta: 1,
      drawMajorGridLines: false,
      drawMinorGridLines: false,
      drawMinorTickLines: false,
      drawMajorBands: false,
      labelStyle: WORK_ORDERS_ROW_TOTAL_TEXT,
    });

    totalAxis.labelProvider.formatLabel = (val: number) => {
      const r = Math.round(val);
      if (r < 0 || r >= apiData.length) return '';
      const totals = sumRowTotals(apiData[r]);
      if (!totals) return '';
      return `${totals.actual}/${totals.expected}`;
    };

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);
    sciChartSurface.yAxes.add(totalAxis);

    /** Alternating row backgrounds aligned to label center (Y=row), spanning full plot width */
    const ROW_BAND_FILL = 'rgba(50, 50, 50, 0.04)';
    for (let i = 0; i < rowCount; i++) {
      if (i % 2 !== 0) continue;
      sciChartSurface.annotations.add(
        new BoxAnnotation({
          x1: 0,
          x2: 1,
          xCoordinateMode: ECoordinateMode.Relative,
          y1: i - 0.5,
          y2: i + 0.5,
          xAxisId: 'xDate',
          yAxisId: 'yAssets',
          fill: ROW_BAND_FILL,
          stroke: 'transparent',
          strokeThickness: 0,
        })
      );
    }

    /** Y span of each schedule bar; keep under 1 Y unit so adjacent rows do not overlap */
    const ROW_BAR_HEIGHT = 0.6;
    const HALF = ROW_BAR_HEIGHT / 2;
    const SEGMENT_STROKE = 0;
    const WINDOW_STROKE = 3;
    const HOVER_SEGMENT_STROKE = 1;
    const HOVER_WINDOW_STROKE = 3;

    /**
     * Visual separation between adjacent windows, kept constant in screen pixels
     * (re-applied on zoom). Geometry is built at real time coords; remapGap() insets each
     * window by WINDOW_GAP_PX/2 px per side and affine-remaps its segments into the inset span.
     */
    const WINDOW_GAP_PX = 6;
    /** Never inset more than this fraction of a window per side, so short windows never collapse. */
    const WINDOW_GAP_MAX_FRAC = 0.3;
    const bandRefs: { ds: XyyDataSeries; seg: SegmentInternal; yLow: number; yHigh: number }[] = [];
    const segBoxRefs: { box: BoxAnnotation; text: TextAnnotation; seg: SegmentInternal }[] = [];
    /** Late count labels (per coloured band) — no per-segment hit box, so late stays one unit. */
    const lateTextRefs: { text: TextAnnotation; seg: SegmentInternal }[] = [];
    const borderRefs: { box: BoxAnnotation; win: ScheduleWindowInternal }[] = [];
    const lateBorderRefs: {
      top: LineAnnotation;
      bottom: LineAnnotation;
      left: LineAnnotation;
      right: LineAnnotation;
      win: ScheduleWindowInternal;
    }[] = [];
    const winByKey = new Map<string, ScheduleWindowInternal>();
    [...windows, ...lateWindows].forEach((w) => winByKey.set(`${w.row}|${w.index}`, w));

    /**
     * A window and its late region read as ONE continuous bar — no gap at their shared junction.
     * `false` on a side means "don't inset there"; the outer edges still get the normal window gap.
     */
    const gapSides = new Map<string, { left: boolean; right: boolean }>();
    lateWindows.forEach((lw) => {
      const meta = lateMetaByKey.get(`${lw.row}|${lw.index}`);
      if (!meta) return;
      /** No gap on late: it flows out of its window (left) and its segments must align to real dates
       *  (right) so the done/pending split lands exactly on the TODAY line. */
      gapSides.set(`${lw.row}|${lw.index}`, { left: false, right: false });
      gapSides.set(`${lw.row}|${meta.parentIndex}`, { left: true, right: false });
    });
    const sidesFor = (row: number, index: string | number) =>
      gapSides.get(`${row}|${index}`) ?? { left: true, right: true };

    /** Bands — SciChartOverview mirrors renderable series (annotations alone produce an empty overview). */
    for (const seg of allSegments) {
      const yLow = seg.row - HALF;
      const yHigh = seg.row + HALF;
      const bandDs = new XyyDataSeries(wasmContext);
      bandDs.appendRange([seg.start, seg.end], [yLow, yLow], [yHigh, yHigh]);
      bandRefs.push({ ds: bandDs, seg, yLow, yHigh });
      /** Late pending is purple (set apart from on-time pending blue); late done-late keeps its colours. */
      const { fill, stroke } =
        isLateSeg(seg) && seg.status === 'pending'
          ? WORK_ORDERS_LATE_PENDING_COLOR
          : STATUS_COLORS[seg.status];
      sciChartSurface.renderableSeries.add(
        new FastBandRenderableSeries(wasmContext, {
          dataSeries: bandDs,
          xAxisId: 'xDate',
          yAxisId: 'yAssets',
          fill,
          fillY1: fill,
          stroke,
          strokeY1: stroke,
          strokeThickness: SEGMENT_STROKE,
        })
      );
    }

    const bridgeY = rowCount > 0 ? (rowCount - 1) / 2 : 0;

    if (allSegments.length === 0) {
      sciChartSurface.renderableSeries.add(
        new FastLineRenderableSeries(wasmContext, {
          id: 'workOrdersOverviewBridge',
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: [sharedXRange.min, sharedXRange.max],
            yValues: [bridgeY, bridgeY],
          }),
          xAxisId: 'xDate',
          yAxisId: 'yAssets',
          stroke: 'rgba(148, 163, 184, 0.35)',
          strokeThickness: 1,
        })
      );
    }

    /**
     * Invisible spanner across the full navigable range so the SciChartOverview (which auto-ranges to
     * the series data) covers the whole range — not just where the bars sit. Without it, dragging the
     * overview snaps the chart to the data extent.
     */
    sciChartSurface.renderableSeries.add(
      new FastLineRenderableSeries(wasmContext, {
        id: 'workOrdersOverviewSpan',
        dataSeries: new XyDataSeries(wasmContext, {
          xValues: [xRangeLimit.min, xRangeLimit.max],
          yValues: [bridgeY, bridgeY],
        }),
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        stroke: 'transparent',
        strokeThickness: 0,
      })
    );

    const findWindow = (row: number, windowIndex: string | number) =>
      windows.find((w) => w.row === row && w.index === windowIndex);

    const fmtDateTime = (sec: number) => {
      const d = new Date(sec * 1000);
      return `${d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz })}`;
    };

    const fmtDate = (sec: number) => {
      const d = new Date(sec * 1000);
      return d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz });
    };

    const getWindowWorkOrder = (row: number, windowIndex: string | number): string | undefined => {
      const group = apiData[row];
      const cw = group?.workOrders.find((w) => w.index === windowIndex);
      const wo = cw?.actions.find((a) => a.workOrderNumber?.trim())?.workOrderNumber?.trim();
      return wo || undefined;
    };

    const STATUS_LABEL: Record<StatusWithinWindow, string> = {
      compliant: 'Compliant',
      partial: 'Partial',
      nonCompliant: 'Non-Compliant',
      pending: 'Pending',
    };

    const tooltipEl = document.createElement('div');
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.display = 'none';
    tooltipEl.style.padding = '6px 10px';
    tooltipEl.style.borderRadius = '4px';
    tooltipEl.style.background = 'rgba(0,0,0,0.8)';
    tooltipEl.style.color = '#fff';
    tooltipEl.style.fontSize = '11px';
    tooltipEl.style.lineHeight = '1.6';
    tooltipEl.style.zIndex = '10';
    if (rootEl instanceof HTMLElement) {
      rootEl.appendChild(tooltipEl);
    }

    const hoverableAnnotations: WorkOrdersHoverableAnnotation[] = [];
    let currentlyHoveredAnnotation: WorkOrdersHoverableAnnotation | undefined;

    const createSegmentBox = (seg: SegmentInternal) => {
      const { stroke } = STATUS_COLORS[seg.status];
      const box = new BoxAnnotation({
        x1: seg.start,
        x2: seg.end,
        y1: seg.row - HALF,
        y2: seg.row + HALF,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        /** Fill from FastBandRenderableSeries; box for hit-test + outline */
        fill: 'transparent',
        stroke,
        strokeThickness: SEGMENT_STROKE,
      }) as WorkOrdersBoxAnnotation;
      box.baseFill = 'transparent';
      box.baseStroke = stroke;
      box.baseStrokeThickness = SEGMENT_STROKE;
      box.workOrdersMeta = {
        kind: 'segment',
        index: seg.index,
        assetId: seg.assetId,
        row: seg.row,
        value: seg.value,
        status: seg.status,
        start: seg.start,
        end: seg.end,
      };
      hoverableAnnotations.push(box);
      sciChartSurface.annotations.add(box);
      box.clicked.subscribe(() => {
        /** Late segments carry the synthetic `#late` index — route the click to the parent window. */
        const lateMeta = lateMetaByKey.get(`${seg.row}|${seg.index}`);
        const targetIndex = lateMeta ? lateMeta.parentIndex : seg.index;
        const w = findWindow(seg.row, targetIndex);
        if (w) emitScheduleClickPayload(w, apiData, onScheduleClick);
      });

      const text = new TextAnnotation({
        x1: (seg.start + seg.end) / 2,
        y1: seg.row,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        text: seg.value.toString(),
        fontSize: 11,
        textColor: '#000',
        horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
        verticalAnchorPoint: EVerticalAnchorPoint.Center,
      });
      sciChartSurface.annotations.add(text);
      segBoxRefs.push({ box, text, seg });
    };

    const createWindowBorder = (w: ScheduleWindowInternal, color: string) => {
      const box = new BoxAnnotation({
        x1: w.start,
        x2: w.end,
        y1: w.row - HALF,
        y2: w.row + HALF,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        fill: 'transparent',
        stroke: color,
        strokeThickness: WINDOW_STROKE,
      }) as WorkOrdersBoxAnnotation;
      box.baseFill = 'transparent';
      box.baseStroke = color;
      box.baseStrokeThickness = WINDOW_STROKE;
      box.workOrdersMeta = {
        kind: 'window',
        index: w.index,
        assetId: w.assetId,
        row: w.row,
        active: w.active,
        expected: w.expected,
        unschedule: w.unschedule,
        windowStart: w.start,
        windowEnd: w.end,
      };
      hoverableAnnotations.push(box);
      sciChartSurface.annotations.add(box);
      borderRefs.push({ box, win: w });
      box.clicked.subscribe(() => emitScheduleClickPayload(w, apiData, onScheduleClick));
    };

    /** Count label for one late coloured band (no hit box — the region-wide box handles interaction). */
    const createLateSegmentText = (seg: SegmentInternal) => {
      const text = new TextAnnotation({
        x1: (seg.start + seg.end) / 2,
        y1: seg.row,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        text: seg.value.toString(),
        fontSize: 11,
        textColor: '#000',
        horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
        verticalAnchorPoint: EVerticalAnchorPoint.Center,
      });
      sciChartSurface.annotations.add(text);
      lateTextRefs.push({ text, seg });
    };

    const LATE_HOVER_FILL = 'rgba(0,0,0,0.06)';
    /** Single hit/hover/click target spanning a whole late region, so late acts as one work order. */
    const createLateHitBox = (w: ScheduleWindowInternal) => {
      const box = new BoxAnnotation({
        x1: w.start,
        x2: w.end,
        y1: w.row - HALF,
        y2: w.row + HALF,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        fill: 'transparent',
        stroke: 'transparent',
        strokeThickness: 0,
      }) as WorkOrdersBoxAnnotation;
      box.baseFill = 'transparent';
      box.baseStroke = 'transparent';
      box.baseStrokeThickness = 0;
      box.workOrdersMeta = {
        kind: 'window',
        index: w.index,
        assetId: w.assetId,
        row: w.row,
        active: w.active,
        expected: w.expected,
        unschedule: w.unschedule,
        windowStart: w.start,
        windowEnd: w.end,
      };
      hoverableAnnotations.push(box);
      sciChartSurface.annotations.add(box);
      borderRefs.push({ box, win: w });
      box.clicked.subscribe(() => {
        const meta = lateMetaByKey.get(`${w.row}|${w.index}`);
        const parent = meta ? findWindow(w.row, meta.parentIndex) : undefined;
        if (parent) emitScheduleClickPayload(parent, apiData, onScheduleClick);
      });
    };

    /**
     * Late region outline: a dashed rectangle drawn as 4 {@link LineAnnotation}s (BoxAnnotation can't dash).
     * Visual only — interaction is handled by the region-wide hit box ({@link createLateHitBox}).
     */
    const LATE_DASH = [6, 4];
    const createLateDashedBorder = (w: ScheduleWindowInternal) => {
      const yTop = w.row + HALF;
      const yBot = w.row - HALF;
      const mk = (x1: number, y1: number, x2: number, y2: number) =>
        new LineAnnotation({
          x1, y1, x2, y2,
          xAxisId: 'xDate',
          yAxisId: 'yAssets',
          stroke: WORK_ORDERS_LATE_BORDER_STROKE,
          strokeThickness: WINDOW_STROKE,
          strokeDashArray: LATE_DASH,
        });
      const top = mk(w.start, yTop, w.end, yTop);
      const bottom = mk(w.start, yBot, w.end, yBot);
      const left = mk(w.start, yBot, w.start, yTop);
      const right = mk(w.end, yBot, w.end, yTop);
      [top, bottom, left, right].forEach((l) => sciChartSurface.annotations.add(l));
      lateBorderRefs.push({ top, bottom, left, right, win: w });
    };

    const UNSCHEDULE_CIRCLE_D = 28;
    const UNSCHEDULE_CIRCLE_R = UNSCHEDULE_CIRCLE_D / 2;
    apiData.forEach((group, rowIndex) => {
      (group.unscheduleEvents ?? []).forEach((eventGroup) => {
        const centerX = dateToSeconds(new Date(eventGroup.occurred));
        const count = eventGroup.events.length;
        /** RFID of all zeros (or missing) = untracked greasing; a real tag = unscheduled event */
        const isUntrackedRfid = (rfid?: string) => !rfid || /^0+$/.test(rfid);
        const untrackedCount = eventGroup.events.filter((e) => isUntrackedRfid(e.rfid)).length;
        const unscheduledCount = count - untrackedCount;

        const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${UNSCHEDULE_CIRCLE_D}" height="${UNSCHEDULE_CIRCLE_D}">
          <circle cx="${UNSCHEDULE_CIRCLE_R}" cy="${UNSCHEDULE_CIRCLE_R}" r="${UNSCHEDULE_CIRCLE_R - 1.5}" fill="#fecaca" stroke="#dc2626" stroke-width="2"/>
          <text x="${UNSCHEDULE_CIRCLE_R}" y="${UNSCHEDULE_CIRCLE_R + 4}" text-anchor="middle" fill="#991b1b" font-family="Roboto,sans-serif" font-weight="bold" font-size="11">${count}</text>
        </svg>`;

        const badge = new CustomAnnotation({
          x1: centerX,
          y1: rowIndex,
          xAxisId: 'xDate',
          yAxisId: 'yAssets',
          svgString,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Center,
          verticalAnchorPoint: EVerticalAnchorPoint.Center,
        }) as WorkOrdersHoverableAnnotation;

        badge.workOrdersMeta = {
          kind: 'untracked',
          row: rowIndex,
          occurred: centerX,
          count,
          untrackedCount,
          unscheduledCount,
        };

        hoverableAnnotations.push(badge);

        badge.clicked.subscribe(() =>
          onUnscheduleGroupClick?.({
            assetId: group.assetId,
            assetName: group.assetName ?? undefined,
            scheduleName: group.scheduleName ?? undefined,
            row: rowIndex,
            unscheduleEvents: [eventGroup],
          })
        );

        sciChartSurface.annotations.add(badge);
      });
    });

    allSegments.forEach((seg) => {
      if (isLateSeg(seg)) createLateSegmentText(seg);
      else createSegmentBox(seg);
    });

    windows
      .filter((w) => w.active)
      .forEach((w) => createWindowBorder(w, WORK_ORDERS_WINDOW_BORDER_STROKE.active));
    windows
      .filter((w) => !w.active)
      .forEach((w) => createWindowBorder(w, WORK_ORDERS_WINDOW_BORDER_STROKE.inactive));

    /** Late regions: coloured bands + counts above, one dashed outline + one region-wide hit box. */
    lateWindows.forEach(createLateDashedBorder);
    lateWindows.forEach(createLateHitBox);

    /** Seconds-per-side inset for a window, derived from the current px scale and clamped. */
    const insetSecFor = (w: ScheduleWindowInternal, secPerPx: number): number => {
      const winSpan = w.end - w.start;
      if (winSpan <= 0) return 0;
      return Math.min((WINDOW_GAP_PX / 2) * secPerPx, winSpan * WINDOW_GAP_MAX_FRAC);
    };

    /** Re-apply the constant-pixel window gap for the current zoom. Idempotent (remaps from real coords). */
    const remapGap = () => {
      const plotW = sciChartSurface.seriesViewRect?.width ?? 0;
      const visDiff = xAxis.visibleRange.diff;
      if (plotW <= 0 || visDiff <= 0) return;
      const secPerPx = visDiff / plotW;

      const remapSeg = (seg: SegmentInternal): { s: number; e: number } => {
        const w = winByKey.get(`${seg.row}|${seg.index}`);
        if (!w) return { s: seg.start, e: seg.end };
        const winSpan = w.end - w.start;
        if (winSpan <= 0) return { s: seg.start, e: seg.end };
        const sides = sidesFor(w.row, w.index);
        const base = insetSecFor(w, secPerPx);
        const insetL = sides.left ? base : 0;
        const insetR = sides.right ? base : 0;
        const scale = (winSpan - insetL - insetR) / winSpan;
        return {
          s: w.start + insetL + (seg.start - w.start) * scale,
          e: w.start + insetL + (seg.end - w.start) * scale,
        };
      };

      borderRefs.forEach(({ box, win }) => {
        const sides = sidesFor(win.row, win.index);
        const base = insetSecFor(win, secPerPx);
        box.x1 = win.start + (sides.left ? base : 0);
        box.x2 = win.end - (sides.right ? base : 0);
      });
      lateBorderRefs.forEach(({ top, bottom, left, right, win }) => {
        const sides = sidesFor(win.row, win.index);
        const base = insetSecFor(win, secPerPx);
        const x1 = win.start + (sides.left ? base : 0);
        const x2 = win.end - (sides.right ? base : 0);
        top.x1 = x1; top.x2 = x2;
        bottom.x1 = x1; bottom.x2 = x2;
        left.x1 = x1; left.x2 = x1;
        right.x1 = x2; right.x2 = x2;
      });
      segBoxRefs.forEach(({ box, text, seg }) => {
        const { s, e } = remapSeg(seg);
        box.x1 = s;
        box.x2 = e;
        text.x1 = (s + e) / 2;
      });
      lateTextRefs.forEach(({ text, seg }) => {
        const { s, e } = remapSeg(seg);
        text.x1 = (s + e) / 2;
      });
      bandRefs.forEach(({ ds, seg, yLow, yHigh }) => {
        const { s, e } = remapSeg(seg);
        ds.clear();
        ds.appendRange([s, e], [yLow, yLow], [yHigh, yHigh]);
      });
    };

    xAxis.visibleRangeChanged.subscribe(() => remapGap());
    /** First layout: seriesViewRect is only valid once rendered; apply the gap once then detach. */
    let didInitGap = false;
    const initGapSub = sciChartSurface.rendered.subscribe(() => {
      if (didInitGap) return;
      didInitGap = true;
      remapGap();
    });
    void initGapSub;

    const todayX = dateToSeconds(new Date());

    sciChartSurface.annotations.add(
      new VerticalLineAnnotation({ x1: todayX, xAxisId: 'xDate', yAxisId: 'yAssets', stroke: '#000', strokeThickness: 3, strokeDashArray: [4, 4] })
    );
    sciChartSurface.annotations.add(
      new TextAnnotation({
        x1: todayX,
        y1: rowCount + 0.3,
        xAxisId: 'xDate',
        yAxisId: 'yAssets',
        text: 'TODAY',
        fontSize: 10,
        textColor: '#000',
      })
    );

    const annotationHoverModifier = new AnnotationHoverModifier({
      enableHover: true,
      targets: hoverableAnnotations,
      hoverMode: EHoverMode.AbsoluteTopmost,
      notifyOutEvent: true,
      notifyPositionUpdate: true,
      onHover: (args) => {
        const hovered = args.hoveredEntities as WorkOrdersHoverableAnnotation[];
        const unhovered = args.unhoveredEntities as WorkOrdersHoverableAnnotation[];

        unhovered.forEach((ann) => {
          if (!(ann instanceof BoxAnnotation)) return;
          const box = ann as WorkOrdersBoxAnnotation;
          box.fill = box.baseFill ?? box.fill;
          box.stroke = box.baseStroke ?? box.stroke;
          box.strokeThickness = box.baseStrokeThickness ?? box.strokeThickness;
        });

        hovered.forEach((ann) => {
          if (!(ann instanceof BoxAnnotation)) return;
          const box = ann as WorkOrdersBoxAnnotation;
          if (box.workOrdersMeta?.kind === 'segment') {
            box.strokeThickness = HOVER_SEGMENT_STROKE;
          } else if (box.workOrdersMeta?.kind === 'window') {
            /** Late hit boxes have a transparent stroke — highlight the whole region with a fill instead. */
            if (lateMetaByKey.has(`${box.workOrdersMeta.row}|${box.workOrdersMeta.index}`)) {
              box.fill = LATE_HOVER_FILL;
            } else {
              box.strokeThickness = HOVER_WINDOW_STROKE;
            }
          }
        });

        currentlyHoveredAnnotation = hovered[0];

        const mousePoint = args.mouseArgs?.mousePoint;
        const hoveredMeta = currentlyHoveredAnnotation?.workOrdersMeta;

        if (rootEl instanceof HTMLElement) {
          rootEl.style.cursor = hoveredMeta ? 'pointer' : '';
        }

        if (!mousePoint || !hoveredMeta) {
          tooltipEl.style.display = 'none';
          return;
        }

        if (hoveredMeta.kind === 'segment') {
          const w = findWindow(hoveredMeta.row, hoveredMeta.index);
          const dateRange = w ? `${fmtDateTime(w.start)} – ${fmtDateTime(w.end)}` : '';
          const wo = getWindowWorkOrder(hoveredMeta.row, hoveredMeta.index);
          tooltipEl.innerHTML = `<strong>${STATUS_LABEL[hoveredMeta.status]}: ${hoveredMeta.value}</strong>${wo ? `<br>${wo}` : ''}${dateRange ? `<br>${dateRange}` : ''}`;
        } else if (hoveredMeta.kind === 'window') {
          /** Late regions share the same tooltip style as normal windows (date range + WO). */
          const lateMeta = lateMetaByKey.get(`${hoveredMeta.row}|${hoveredMeta.index}`);
          const wo = getWindowWorkOrder(hoveredMeta.row, lateMeta ? lateMeta.parentIndex : hoveredMeta.index);
          tooltipEl.innerHTML = `<strong>${fmtDateTime(hoveredMeta.windowStart)} – ${fmtDateTime(hoveredMeta.windowEnd)}</strong>${wo ? `<br>${wo}` : ''}`;
        } else {
          const lines = [`<strong>${fmtDate(hoveredMeta.occurred)}</strong>`];
          if (hoveredMeta.untrackedCount > 0) lines.push(`Untracked Greasing: ${hoveredMeta.untrackedCount}`);
          if (hoveredMeta.unscheduledCount > 0) lines.push(`Unscheduled Events: ${hoveredMeta.unscheduledCount}`);
          tooltipEl.innerHTML = lines.join('<br>');
        }

        /** mousePoint is in canvas/device pixels; divide by DPR to get CSS pixels for DOM positioning. */
        const dpr = window.devicePixelRatio || 1;
        tooltipEl.style.left = `${mousePoint.x / dpr + 12}px`;
        tooltipEl.style.top = `${mousePoint.y / dpr + 12}px`;
        tooltipEl.style.display = 'block';
      },
    });

    annotationHoverModifier.hoverChanged.subscribe((args) => {
      if (!args?.includedEntities?.length) {
        currentlyHoveredAnnotation = undefined;
        tooltipEl.style.display = 'none';
        if (rootEl instanceof HTMLElement) {
          rootEl.style.cursor = '';
        }
      }
    });

    const tooltipTemplate: TCursorTooltipDataTemplate = () => [];

    addSharedWorkOrdersXZoomModifiers(sciChartSurface);
    sciChartSurface.chartModifiers.add(
      new CursorModifier({
        showTooltip: false,
        tooltipDataTemplate: tooltipTemplate,
        showXLine: false,
        showYLine: false,
      }),
      annotationHoverModifier
    );

    return { sciChartSurface };
  };

  return { initHeaderChart, initBodyChart };
}
